import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'

type MutedOrgRow = {
  id: string
  slug: string
  name: string
  logo_url: string | null
  muted: boolean
}

type Props = {
  organizations: MutedOrgRow[]
}

export default function MutedOrganizations({ organizations }: Props) {
  // Mirror props in local state so the Switch flips instantly on click;
  // re-sync whenever Inertia re-renders the page with fresh server truth.
  const [rows, setRows] = useState(organizations)
  useEffect(() => {
    setRows(organizations)
  }, [organizations])

  function setMuted(orgId: string, muted: boolean) {
    setRows(rs => rs.map(r => (r.id === orgId ? { ...r, muted } : r)))
  }

  function toggle(org: MutedOrgRow, emailsOn: boolean) {
    setMuted(org.id, !emailsOn)
    if (emailsOn) {
      router.delete(`/organization_post_mutes/${org.id}`, {
        preserveScroll: true,
        onError: () => setMuted(org.id, true),
      })
    } else {
      router.post(
        '/organization_post_mutes',
        { organization_id: org.id },
        { preserveScroll: true, onError: () => setMuted(org.id, false) }
      )
    }
  }

  return (
    <>
      <Head title="Muted organizations" />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            href="/settings"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back to settings
          </Link>
        </div>
        <h1 className="text-4xl font-bold tracking-tight">Muted organizations</h1>
        <p className="mt-3 text-base text-muted-foreground">
          Turn off email notifications for posts from specific organizations. You&apos;ll still see their posts in your feed.
        </p>

        <div className="mt-8">
          {rows.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
                <p className="text-base text-muted-foreground">
                  Follow organizations to manage email preferences here.
                </p>
                <Button asChild>
                  <Link href="/events">Browse events</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <ul className="divide-y divide-black/5">
                  {rows.map(org => {
                    const emailsOn = !org.muted
                    return (
                      <li
                        key={org.id}
                        className="flex items-center justify-between gap-4 px-4 py-4 sm:px-6"
                      >
                        <Link
                          href={`/o/${org.slug}`}
                          className="flex min-w-0 flex-1 items-center gap-3 hover:text-primary"
                        >
                          <Avatar className="h-10 w-10 shrink-0">
                            {org.logo_url && <AvatarImage src={org.logo_url} alt={org.name} />}
                            <AvatarFallback>{org.name[0]}</AvatarFallback>
                          </Avatar>
                          <span className="truncate text-base font-medium">{org.name}</span>
                        </Link>
                        <div className="flex shrink-0 items-center gap-3">
                          <span className="hidden text-sm text-muted-foreground sm:inline">
                            {emailsOn ? 'Emails on' : 'Muted'}
                          </span>
                          <Switch
                            checked={emailsOn}
                            onCheckedChange={checked => toggle(org, checked)}
                            aria-label={`Email me about new posts from ${org.name}`}
                          />
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}
